import { gql, type TypedDocumentNode } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import styled, { type CSSProperties } from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase/index.tsx';
import { Ball } from '@sorare/core/src/atoms/icons/Ball.tsx';
import { ChevronRightBold } from '@sorare/core/src/atoms/icons/ChevronRightBold.tsx';
import {
  Horizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex/index.tsx';
import {
  BrandM,
  BrandXS,
  BrandXXS,
} from '@sorare/core/src/atoms/typography/index.tsx';
import { EdgeRectangleTrapezoid } from '@sorare/core/src/atoms/ui/EdgeRectangleTrapezoid/index.tsx';
import {
  AnyRewardConfig,
  Variant,
} from '@sorare/core/src/components/rewards/AnyRewardConfig/index.tsx';
import { useIntlContext } from '@sorare/core/src/contexts/intl/index.ts';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount.ts';
import { getEdgeRectangleShapeRarityGradients } from '@sorare/core/src/lib/colors.ts';
import { fantasy } from '@sorare/core/src/lib/glossary.ts';
import { isType } from '@sorare/core/src/lib/gql';
import { monetaryAmountFragment } from '@sorare/core/src/lib/monetaryAmount.ts';
import { absCenter } from '@sorare/core/src/lib/style.ts';

import streakBackground from '#src/assets/streakBackground.webp';
import { ThresholdIcon } from '#src/components/FixtureCompetitionTrckPage/TrckGroupPVECompetitionPrizePool/ThresholdIcon/index.tsx';

import type { ContenderPveFooter_so5LeaderboardContender } from './__generated__/index.graphql.ts';

const Root = styled(Vertical).attrs({ gap: 2 })`
  align-items: center;
  border-radius: var(--unit);
  background: var(--c-nd-100);
  border: 1px solid transparent;
  padding: var(--double-unit) var(--double-unit) 0 var(--double-unit);

  &.threshold-reached {
    border: 1px solid transparent;
    background:
      url(${streakBackground}) center / cover padding-box,
      linear-gradient(
          133deg,
          rgba(255, 78, 34, 1) 0%,
          rgba(254, 243, 20, 1) 100%
        )
        border-box;
  }
`;
const BarWrapper = styled(Horizontal).attrs({ center: true })`
  border-radius: var(--unit);
  height: 50px;
  background: linear-gradient(
      to right,
      var(--color) calc(var(--progress) - 2px),
      var(--c-white) var(--progress),
      transparent calc(var(--progress) + 1px)
    ),
    var(--c-nd-100-opaque);
`;
const GoldenGoalIconWrapper = styled.div`
  position: relative;
  width: var(--double-unit);
  height: var(--double-unit);

  & > * {
    ${absCenter};
  }
`;
const GoldenGoalBall = styled(Ball)`
  fill: #cc8cd6;
`;
const Exponent = styled(BrandXXS)`
  margin-top: var(--half-unit);
`;
const ShadowedBrandXXS = styled(BrandXXS)`
  text-shadow: 0px 0px 1px var(--c-black);
`;

type Props = {
  onClick?: () => void;
  so5LeaderboardContender: ContenderPveFooter_so5LeaderboardContender;
};
export const ContenderPveFooter = ({
  onClick,
  so5LeaderboardContender,
}: Props) => {
  const { formatNumber } = useIntlContext();
  const { toMonetaryAmount } = useMonetaryAmount();
  const { so5Leaderboard, so5Lineup } = so5LeaderboardContender;
  const {
    myTasks,
    so5League,
    rewardsConfig,
    so5LeaderboardGroup: { myEligibleOrSo5Rewards },
  } = so5Leaderboard || {};
  const goldenGoalRewardConfig = rewardsConfig.conditional?.at(0) || undefined;
  const goldenGoalAnyRewardConfig =
    goldenGoalRewardConfig?.rewardConfigs.at(0) || undefined;

  const goldenGoalAnyRewardConfigForCurrentUser = isType(
    goldenGoalAnyRewardConfig,
    'CardShardRewardConfig'
  )
    ? {
        ...goldenGoalAnyRewardConfig,
        sharedPool: false,
        poolSize: null,
        quantity:
          goldenGoalAnyRewardConfig.quantity /
          (goldenGoalRewardConfig?.poolSize || 1),
      }
    : isType(goldenGoalAnyRewardConfig, 'MonetaryRewardConfig')
      ? {
          ...goldenGoalAnyRewardConfig,
          sharedPool: false,
          poolSize: null,
          amount: toMonetaryAmount(
            goldenGoalAnyRewardConfig.monetaryAmount
          ).divideBy(goldenGoalRewardConfig?.poolSize || 1),
        }
      : goldenGoalAnyRewardConfig;

  const [task] = myTasks || [];
  if (!isType(task, 'ThresholdsStreakTask')) {
    return null;
  }

  const { thresholds } = task;
  const currentThresholdIndex = thresholds.findIndex(
    ({ current }) => !!current
  );
  const currentThreshold = thresholds[currentThresholdIndex];
  const score = so5Lineup?.so5Rankings.at(0)?.score || 0;
  const progress = (score / (currentThreshold?.score || 1)) * 100;
  const ptsToTarget = currentThreshold.score - score;
  const goldenGoalCompleted = myEligibleOrSo5Rewards.some(
    ({ id }) => id === goldenGoalRewardConfig?.id
  );

  const scoreDisplay = (
    <Horizontal>
      <BrandM as={Horizontal}>
        <FormattedMessage
          {...fantasy.ordinalPoints}
          values={{
            score: formatNumber(score, {
              maximumFractionDigits: 0,
            }),
            sup: content => (
              <Exponent style={{ color: 'var(--c-nd-600)' }}>
                {content}
              </Exponent>
            ),
          }}
        />
      </BrandM>
      <ChevronRightBold color="var(--c-nd-600)" />
    </Horizontal>
  );
  const currentCompletedTask = so5Lineup?.completedTasks?.at(0);
  const currentCompletedTaskReward = currentCompletedTask?.rewardConfigs.at(0);

  if (!currentCompletedTask) {
    return (
      <Vertical>
        <ButtonBase onClick={onClick}>
          <Vertical>
            <BarWrapper
              style={
                {
                  '--color': so5League.tertiaryColor,
                  '--progress': `${progress}%`,
                } as CSSProperties
              }
            >
              {scoreDisplay}
            </BarWrapper>
            {ptsToTarget > 0 && (
              <BrandXS>
                <FormattedMessage
                  id="ContenderPveFooter.toTarget"
                  defaultMessage="{n} pts <secondary>to target</secondary>"
                  values={{
                    n: ptsToTarget.toFixed(0),
                    secondary: content => (
                      <span style={{ color: 'var(--c-nd-600)' }}>
                        {content}
                      </span>
                    ),
                  }}
                />
              </BrandXS>
            )}
          </Vertical>
        </ButtonBase>
      </Vertical>
    );
  }

  const hasAdditionalLineupTask = isType(
    currentCompletedTask,
    'ThresholdsStreakAdditionalLineupTask'
  );
  const gradientColors = getEdgeRectangleShapeRarityGradients(
    so5Leaderboard.mainRarityType || 'mix'
  );

  return (
    <ButtonBase onClick={onClick}>
      <Root
        className={classNames({ 'threshold-reached': !!currentCompletedTask })}
      >
        {scoreDisplay}
        {!!currentCompletedTaskReward && (
          <EdgeRectangleTrapezoid
            padding="2px var(--triple-unit)"
            offset="20px"
            background={gradientColors.bg}
            border={gradientColors.border}
            hoverBackground={gradientColors.hoverBg}
          >
            <Horizontal>
              {!!goldenGoalCompleted &&
                !!goldenGoalAnyRewardConfigForCurrentUser && (
                  <>
                    <Horizontal>
                      <AnyRewardConfig
                        variant={Variant.TEXT}
                        anyRewardConfig={
                          goldenGoalAnyRewardConfigForCurrentUser
                        }
                      />
                      <GoldenGoalIconWrapper>
                        <GoldenGoalBall />
                        <ShadowedBrandXXS>
                          {goldenGoalRewardConfig?.poolSize}
                        </ShadowedBrandXXS>
                      </GoldenGoalIconWrapper>
                    </Horizontal>
                    <svg
                      width="2"
                      height="10"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <line
                        x1="1"
                        y1="0"
                        x2="1"
                        y2="100%"
                        stroke="var(--c-nd-50)"
                        strokeWidth="1"
                      />
                    </svg>
                  </>
                )}
              <Horizontal>
                <AnyRewardConfig
                  variant={Variant.TEXT}
                  anyRewardConfig={currentCompletedTaskReward}
                />
                <ThresholdIcon
                  additionalLineupReward={hasAdditionalLineupTask}
                  currentStreakToDisplay={currentThresholdIndex + 1}
                />
              </Horizontal>
            </Horizontal>
          </EdgeRectangleTrapezoid>
        )}
      </Root>
    </ButtonBase>
  );
};

ContenderPveFooter.fragments = {
  so5LeaderboardContender: gql`
    fragment ContenderPveFooter_so5LeaderboardContender on So5LeaderboardContender {
      slug
      so5Lineup {
        id
        completedTasks {
          id
          ... on ThresholdsStreakTask {
            id
            progress
          }
          ... on ThresholdsStreakAdditionalLineupTask {
            id
            thresholdsStreakTask {
              id
              progress
            }
          }
          rewardConfigs {
            id
            ...AnyRewardConfig_anyRewardConfigInterface
          }
        }
        so5Rankings {
          id
          score
        }
      }
      so5Leaderboard {
        slug
        id
        mainRarityType
        rewardsConfig {
          conditional {
            id
            score
            poolSize
            rewardConfigs {
              id
              ... on CardShardRewardConfig {
                id
                quantity
                poolSize
                sharedPool
              }
              ... on MonetaryRewardConfig {
                id
                poolSize
                sharedPool
                monetaryAmounts: amount {
                  ...MonetaryAmountFragment_monetaryAmount
                }
              }
              ...AnyRewardConfig_anyRewardConfigInterface
            }
          }
        }
        so5League {
          slug
          tertiaryColor: color(variant: OPTIONAL)
        }
        so5LeaderboardGroup {
          slug
          myEligibleOrSo5Rewards {
            ... on So5Reward {
              slug
              id
            }
            ... on So5RewardConfig {
              id
            }
          }
        }
        myTasks {
          id
          ... on ThresholdsStreakTask {
            id
            thresholds {
              current
              score
            }
          }
        }
      }
    }
    ${AnyRewardConfig.fragments.anyRewardConfigInterface}
    ${monetaryAmountFragment}
  ` as TypedDocumentNode<ContenderPveFooter_so5LeaderboardContender>,
};
